/**
 * Shared UI primitives.
 *
 * Deliberately small and unopinionated. Severity/risk/status colour mappings
 * live here so a "critical" issue or a "HIGH" risk action looks identical on
 * every screen — consistency matters more in a change-management tool than
 * visual variety.
 */

import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { cn, confidenceBand, confidenceTone } from '@/utils/format';
import { workspacePresentation } from '@/components/workspacePresentationMeta';
import { ArrowRightIcon, GlobeIcon } from '@/components/icons';
import type {
  ActionRisk,
  ActionStatus,
  ExecutionStatus,
  IssueSeverity,
  JobStatus,
  VerificationStatus,
} from '@/types/api';

// -------------------------------------------------------------------- layout
export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return <div className={cn('card', padded && 'p-5', className)}>{children}</div>;
}

export function CardHeader({
  title,
  description,
  action,
  icon,
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="workspace-card-heading mb-4 flex items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-2.5">
        {icon ? <span className="mt-px shrink-0 text-ink-subtle">{icon}</span> : null}
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {description ? <p className="mt-1 text-sm text-ink-muted">{description}</p> : null}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
  breadcrumb,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
}) {
  const presentation = workspacePresentation(useLocation().pathname);
  const PageIcon = presentation.icon;
  return (
    <header className="page-heading mb-6">
      <div className="workspace-page-kicker"><span><PageIcon /></span>{presentation.label}</div>
      {breadcrumb ? <div className="mb-2 text-sm text-ink-subtle">{breadcrumb}</div> : null}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-[1.75rem] leading-tight font-semibold tracking-tight text-ink">
            {title}
          </h1>
          {description ? (
            <p className="mt-1.5 max-w-3xl text-sm text-ink-muted">{description}</p>
          ) : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </div>
    </header>
  );
}

/**
 * A labelled group of related cards.
 *
 * Section headings do the decluttering work: without them a dashboard is a
 * uniform wall of cards and the reader has to infer which ones belong together.
 */
export function Section({
  title,
  description,
  action,
  children,
  className,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={className}>
      {title ? (
        <div className="mb-3 flex items-end justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-xs font-semibold tracking-wider text-ink-subtle uppercase">
              {title}
            </h2>
            {description ? <p className="mt-1 text-sm text-ink-muted">{description}</p> : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/**
 * A scanning column beside a rail that stays in view.
 *
 * The rail's height is budgeted against the fixed shell — context bar, footer,
 * and its own breathing room — because a sticky element taller than the space
 * left below it does not pin, it gets pushed up out of the scrollport and
 * clipped. Kept in one place so every detail page inherits the working sums
 * rather than re-deriving them.
 */
export function DetailLayout({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('workspace-detail-layout grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_21rem]', className)}>
      {children}
    </div>
  );
}

/** The scanning column of a DetailLayout. */
export function DetailMain({ children }: { children: ReactNode }) {
  return <div className="workspace-detail-main min-w-0 space-y-6">{children}</div>;
}

/** The pinned column of a DetailLayout. */
export function DetailRail({ children }: { children: ReactNode }) {
  return (
    <aside className="workspace-detail-rail pane-scroll min-w-0 space-y-4 xl:sticky xl:top-6 xl:max-h-[calc(100dvh-9rem)] xl:overflow-y-auto xl:pr-1">
      {children}
    </aside>
  );
}

/** The filter row above a list. */
export function FilterBar({ children }: { children: ReactNode }) {
  return (
    <Card padded={false} className="workspace-filter-bar mb-5">
      <div className="flex flex-wrap items-end gap-3 p-4">{children}</div>
    </Card>
  );
}

/** One labelled control inside a FilterBar. */
export function FilterField({
  label,
  htmlFor,
  children,
  className,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="block text-xs font-medium text-ink-subtle">
        {label}
      </label>
      {children}
    </div>
  );
}

/** A row of status chips under a page title. */
export function MetaBar({ children }: { children: ReactNode }) {
  return <div className="mb-6 flex flex-wrap items-center gap-2">{children}</div>;
}

/** "View all →" style link, used wherever a card summarises a longer list. */
export function CardLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="group inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
    >
      {children}
      <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

// ------------------------------------------------------------------- buttons
type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

const buttonStyles: Record<ButtonVariant, string> = {
  primary: 'bg-brand text-white shadow-card hover:bg-brand-hover disabled:bg-brand/50',
  secondary:
    'bg-surface text-ink border border-border-strong shadow-card hover:bg-surface-muted disabled:opacity-50',
  ghost: 'text-ink-muted hover:bg-surface-muted hover:text-ink disabled:opacity-50',
  danger: 'bg-danger text-white shadow-card hover:opacity-90 disabled:opacity-50',
  success: 'bg-success text-white shadow-card hover:opacity-90 disabled:opacity-50',
};

export function Button({
  children,
  variant = 'secondary',
  size = 'md',
  type = 'button',
  className,
  loading = false,
  disabled,
  onClick,
  title,
  icon,
  ariaLabel,
}: {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  type?: 'button' | 'submit';
  className?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  title?: string;
  icon?: ReactNode;
  /** Spell out what a short label means. A button reading "Run" inside a card
      titled "AI analysis" is clear on screen and meaningless out of context. */
  ariaLabel?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      title={title}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors disabled:cursor-not-allowed',
        size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm',
        buttonStyles[variant],
        className,
      )}
    >
      {loading ? <Spinner className="h-3.5 w-3.5" /> : icon}
      {children}
    </button>
  );
}

export function LinkButton({
  to,
  children,
  variant = 'secondary',
  size = 'md',
  icon,
}: {
  to: string;
  children: ReactNode;
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  icon?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors',
        size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm',
        buttonStyles[variant],
      )}
    >
      {icon}
      {children}
    </Link>
  );
}

// -------------------------------------------------------------------- badges
export function Badge({
  children,
  tone = 'neutral',
  className,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'critical' | 'info';
  className?: string;
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-surface-muted text-ink-muted border-border-subtle',
    brand: 'bg-brand-subtle text-brand border-brand-border',
    success: 'bg-success-subtle text-success border-success/25',
    warning: 'bg-warning-subtle text-warning border-warning/30',
    danger: 'bg-danger-subtle text-danger border-danger/25',
    critical: 'bg-critical-subtle text-critical border-critical/30',
    info: 'bg-info-subtle text-info border-info/25',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap',
        tones[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

const severityTone: Record<IssueSeverity, Parameters<typeof Badge>[0]['tone']> = {
  critical: 'critical',
  error: 'danger',
  warning: 'warning',
  opportunity: 'brand',
  info: 'neutral',
};

export function SeverityBadge({ severity }: { severity: IssueSeverity }) {
  return <Badge tone={severityTone[severity]}>{severity}</Badge>;
}

const riskTone: Record<ActionRisk, Parameters<typeof Badge>[0]['tone']> = {
  LOW: 'success',
  MEDIUM: 'warning',
  HIGH: 'danger',
  CRITICAL: 'critical',
};

export function RiskBadge({ risk }: { risk: ActionRisk }) {
  return <Badge tone={riskTone[risk]}>{risk} risk</Badge>;
}

const statusTone: Record<string, Parameters<typeof Badge>[0]['tone']> = {
  // job
  queued: 'neutral',
  running: 'info',
  completed: 'success',
  failed: 'danger',
  cancelled: 'neutral',
  retrying: 'warning',
  partial: 'warning',
  // action
  draft: 'neutral',
  pending_approval: 'warning',
  approved: 'info',
  rejected: 'neutral',
  executing: 'info',
  verifying: 'info',
  rolled_back: 'neutral',
  // execution
  pending: 'neutral',
  validating: 'info',
  applying: 'info',
  succeeded: 'success',
  // verification
  passed: 'success',
  inconclusive: 'warning',
};

export function StatusBadge({
  status,
}: {
  status: JobStatus | ActionStatus | ExecutionStatus | VerificationStatus | string;
}) {
  return <Badge tone={statusTone[status] ?? 'neutral'}>{String(status).replace(/_/g, ' ')}</Badge>;
}

// ------------------------------------------------------------------- feedback
export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin', className ?? 'h-4 w-4')}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
    </svg>
  );
}

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="workspace-loading flex items-center justify-center gap-3 py-16 text-sm text-ink-muted">
      <Spinner />
      <span>{label}…</span>
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <Card className="workspace-error border-danger/30 bg-danger-subtle">
      <h3 className="text-sm font-semibold text-danger">{title}</h3>
      {message ? <p className="mt-1 text-sm text-ink-muted">{message}</p> : null}
      {onRetry ? (
        <Button variant="secondary" size="sm" className="mt-3" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </Card>
  );
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="workspace-empty rounded-xl border border-dashed border-border-strong bg-surface-muted/40 px-6 py-12 text-center">
      <div className="workspace-empty-symbol mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-surface text-ink-subtle shadow-card">{icon ?? <GlobeIcon />}</div>
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {description ? (
        <p className="mx-auto mt-1 max-w-md text-sm text-ink-muted">{description}</p>
      ) : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

export function Callout({
  children,
  tone = 'info',
  title,
  icon,
  action,
}: {
  children: ReactNode;
  tone?: 'info' | 'warning' | 'success' | 'danger';
  title?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  const tones = {
    info: 'border-info/25 bg-info-subtle',
    warning: 'border-warning/30 bg-warning-subtle',
    success: 'border-success/25 bg-success-subtle',
    danger: 'border-danger/25 bg-danger-subtle',
  };
  const iconTones = {
    info: 'text-info',
    warning: 'text-warning',
    success: 'text-success',
    danger: 'text-danger',
  };
  return (
    <div className={cn('rounded-xl border px-4 py-3.5 text-sm', tones[tone])}>
      <div className="flex items-start gap-3">
        {icon ? <span className={cn('mt-px shrink-0', iconTones[tone])}>{icon}</span> : null}
        <div className="min-w-0 flex-1">
          {title ? <p className="font-semibold text-ink">{title}</p> : null}
          <div className={cn('text-ink-muted', title && 'mt-1')}>{children}</div>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------- forms
export function Field({
  label,
  htmlFor,
  hint,
  error,
  children,
  required,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: ReactNode;
  required?: boolean;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-sm font-medium text-ink">
        {label}
        {required ? <span className="ml-0.5 text-danger">*</span> : null}
      </label>
      <div className="mt-1.5">{children}</div>
      {error ? (
        <p className="mt-1 text-xs text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-xs text-ink-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

// --------------------------------------------------------------------- table
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="workspace-table-scroll scroll-x -mx-5 px-5" tabIndex={0} role="region" aria-label="Scrollable data table">
      <table className="w-full min-w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Th({
  children,
  align = 'left',
  className,
}: {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={cn(
        // Without horizontal padding adjacent headings run together, which
        // is how "AFFECTED URLS" and "STATUS" once rendered as one word.
        'border-b border-border-subtle px-3 pb-2 text-xs font-medium tracking-wide text-ink-subtle uppercase first:pl-0 last:pr-0',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        align === 'left' && 'text-left',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  align = 'left',
  className,
}: {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}) {
  return (
    <td
      className={cn(
        'border-b border-border-subtle px-3 py-3 align-top text-ink first:pl-0 last:pr-0',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </td>
  );
}

// ------------------------------------------------------------------ display
const metricValueTone = {
  neutral: 'text-ink',
  danger: 'text-danger',
  warning: 'text-warning',
  success: 'text-success',
  brand: 'text-brand',
};

export function MetricTile({
  label,
  value,
  delta,
  hint,
  tone = 'neutral',
  icon,
}: {
  label: string;
  value: ReactNode;
  delta?: number | null;
  hint?: string;
  tone?: keyof typeof metricValueTone;
  icon?: ReactNode;
}) {
  return (
    <Card className="workspace-metric h-full">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium tracking-wide text-ink-subtle uppercase">{label}</p>
        {icon ? <span className="shrink-0 text-ink-subtle">{icon}</span> : null}
      </div>
      <p className={cn('tnum mt-2 text-2xl font-semibold', metricValueTone[tone])}>{value}</p>
      {delta !== undefined && delta !== null ? (
        <p
          className={cn(
            'tnum mt-1 text-xs font-medium',
            delta > 0 ? 'text-success' : delta < 0 ? 'text-danger' : 'text-ink-subtle',
          )}
        >
          {delta > 0 ? '+' : ''}
          {delta.toFixed(1)}% vs previous period
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-ink-subtle">{hint}</p>
      ) : null}
    </Card>
  );
}

/**
 * A compact counter that links somewhere.
 *
 * Distinct from MetricTile on purpose: these are navigation, and looking
 * identical to a read-only metric is what made the old dashboard read as an
 * undifferentiated wall of tiles.
 */
export function StatLink({
  to,
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
}: {
  to: string;
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: keyof typeof metricValueTone;
  icon?: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-3.5 rounded-xl border border-border-subtle bg-surface p-4 shadow-card transition-colors hover:border-brand-border hover:bg-brand-subtle/40"
    >
      {icon ? (
        <span
          className={cn(
            'grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-surface-muted',
            metricValueTone[tone],
          )}
        >
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-ink">{label}</p>
        {hint ? <p className="mt-0.5 truncate text-xs text-ink-subtle">{hint}</p> : null}
      </div>
      <span className={cn('tnum text-xl font-semibold', metricValueTone[tone])}>{value}</span>
      <ArrowRightIcon className="h-4 w-4 shrink-0 text-ink-subtle transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

/** Label/value pair for the dense summary lists in the sticky rail. */
export function DataRow({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 text-sm">
      <span className="shrink-0 text-ink-muted">{label}</span>
      <span className="flex min-w-0 items-center justify-end gap-2 text-right">{children}</span>
    </div>
  );
}

/** URL rendered so long paths truncate instead of widening the layout. */
export function UrlText({ url, className }: { url: string; className?: string }) {
  let display = url;
  try {
    const parsed = new URL(url);
    display = parsed.pathname + parsed.search || '/';
  } catch {
    display = url;
  }
  return (
    <span className={cn('block truncate font-mono text-xs text-ink-muted', className)} title={url}>
      {display}
    </span>
  );
}

export function ProgressBar({ percent, label }: { percent: number; label?: string }) {
  const clamped = Math.max(0, Math.min(100, percent));
  return (
    <div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label ?? 'Progress'}
      >
        <div
          className="h-full rounded-full bg-brand transition-[width] duration-500"
          style={{ width: `${clamped}%` }}
        />
      </div>
      {label ? <p className="mt-1.5 text-xs text-ink-subtle">{label}</p> : null}
    </div>
  );
}

/**
 * The health score, drawn as a dial.
 *
 * The number alone gave no sense of scale — 81 out of what, and is that good?
 * The arc answers both at a glance while the band underneath says it in words.
 */
export function ScoreDial({
  score,
  band,
  tone,
}: {
  score: number | null;
  band?: string | null;
  tone: 'neutral' | 'success' | 'warning' | 'danger';
}) {
  const radius = 52;
  const circumference = Math.PI * radius; // half circle
  const fraction = score === null ? 0 : Math.max(0, Math.min(100, score)) / 100;
  const strokeTone = {
    neutral: 'text-ink-subtle',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
  }[tone];

  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 128 72" className="w-32" role="img" aria-label={`Health score ${score ?? 'not calculated'} out of 100`}>
        <path
          d="M12 64a52 52 0 0 1 104 0"
          fill="none"
          strokeWidth="10"
          strokeLinecap="round"
          className="stroke-surface-sunken"
        />
        <path
          d="M12 64a52 52 0 0 1 104 0"
          fill="none"
          strokeWidth="10"
          strokeLinecap="round"
          stroke="currentColor"
          className={cn(strokeTone, 'transition-[stroke-dashoffset] duration-700')}
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
        />
      </svg>
      <div className="-mt-6 text-center">
        <p className={cn('tnum text-3xl leading-none font-semibold', metricValueTone[tone])}>
          {score ?? '—'}
        </p>
        <p className="mt-1 text-xs text-ink-subtle">{band ?? 'not calculated'}</p>
      </div>
    </div>
  );
}

/**
 * Confidence as a ring.
 *
 * A number alone invites over-reading. The ring shows how much of the dial is
 * actually filled, and the word underneath says what that means, so a low score
 * reads as a caveat rather than as a grade.
 */
export function ConfidenceRing({
  value,
  size = 'md',
  showBand = true,
}: {
  value: number;
  size?: 'sm' | 'md';
  showBand?: boolean;
}) {
  const box = size === 'sm' ? 52 : 68;
  const radius = size === 'sm' ? 20 : 26;
  const stroke = size === 'sm' ? 5 : 6;
  const circumference = 2 * Math.PI * radius;
  const fraction = Math.max(0, Math.min(1, value));
  const tone = confidenceTone(value);
  const strokeClass = { success: 'text-success', brand: 'text-brand', warning: 'text-warning' }[
    tone
  ];

  return (
    <div className={cn('flex shrink-0 items-center gap-3', size === 'md' && 'sm:flex-col sm:gap-1.5')}>
      <div className="relative" style={{ width: box, height: box }}>
        <svg viewBox={`0 0 ${box} ${box}`} className="h-full w-full -rotate-90">
          <circle
            cx={box / 2}
            cy={box / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            className="stroke-surface-sunken"
          />
          <circle
            cx={box / 2}
            cy={box / 2}
            r={radius}
            fill="none"
            strokeWidth={stroke}
            strokeLinecap="round"
            stroke="currentColor"
            className={cn(strokeClass, 'transition-[stroke-dashoffset] duration-700')}
            strokeDasharray={circumference}
            strokeDashoffset={circumference * (1 - fraction)}
          />
        </svg>
        <span className="absolute inset-0 grid place-items-center">
          <span
            className={cn('tnum font-semibold text-ink', size === 'sm' ? 'text-xs' : 'text-base')}
          >
            {Math.round(fraction * 100)}%
          </span>
        </span>
      </div>
      {showBand ? (
        <div className={cn('text-left', size === 'md' && 'sm:text-center')}>
          <p className="text-[11px] font-medium tracking-wide text-ink-subtle uppercase">
            Confidence
          </p>
          <p className="text-[11px] text-ink-subtle">{confidenceBand(value)}</p>
        </div>
      ) : null}
    </div>
  );
}

/** Before/after comparison, the primary way changes are reviewed. */
export function DiffView({
  before,
  after,
  beforeLabel = 'Current',
  afterLabel = 'Proposed',
}: {
  before: ReactNode;
  after: ReactNode;
  beforeLabel?: string;
  afterLabel?: string;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="rounded-lg border border-border-subtle bg-surface-muted p-3">
        <p className="text-xs font-medium tracking-wide text-ink-subtle uppercase">{beforeLabel}</p>
        <div className="mt-1.5 text-sm break-words text-ink-muted">{before || <em>(empty)</em>}</div>
      </div>
      <div className="rounded-lg border border-success/25 bg-success-subtle p-3">
        <p className="text-xs font-medium tracking-wide text-success uppercase">{afterLabel}</p>
        <div className="mt-1.5 text-sm break-words text-ink">{after || <em>(empty)</em>}</div>
      </div>
    </div>
  );
}
